import { useEffect, useState } from "@bestowinc/tinkertoy-shell-deps/react";
import type { ReactNode } from "@bestowinc/tinkertoy-shell-deps/react";

import {
  FAILURE_EVENT_TAGS,
  isModuleLoadError,
  markFailureReported,
  reportFailureOnce,
  resolveFailureError,
} from "../error-telemetry";
import { useClientActionMap } from "../actions/use-client-action-map";
import type { LoadedModule, ModuleLoader } from "../module-loader";
import { markModuleReadyForReadiness } from "../performance-telemetry";
import { useOptionalViewIdentity } from "../view-identity";

import type {
  ModuleID,
  ModuleInstanceKey,
  VersionString,
} from "@bestowinc/tinkertoy-core";

import { getModuleLoadPromise, peekLoadedModule } from "./module-read-resource";

// ============================================================================
// ModuleRenderer
// ============================================================================

export type ModuleRendererProps = {
  moduleId: ModuleID;
  /**
   * Which of the view's module refs this renders; sent with the module's
   * actions so the server resolves the same ref. Absent for slot modules.
   */
  instanceKey?: ModuleInstanceKey;
  /**
   * Exact module version the renderer mounts (drawn from the loader's
   * `resolvedModuleVersions` map / pinned by the view manifest). Used to
   * fetch the bundle and forwarded to the server on every action POST so
   * it can re-validate the request against the same manifest.
   */
  version: VersionString;
  /** Used to fetch the bundle when this module key is not yet cached. */
  loader: ModuleLoader;

  /** Already-resolved props (ModuleRef callback has been invoked). */
  props: unknown;
  /** Server-fetched data for this module. */
  data: unknown;

  /** For branch modules — pre-rendered slot content. */
  slots?: Record<string, ReactNode>;
  /** For layout modules — main content. */
  children?: ReactNode;

  /** Shown while the bundle is loading (uses the shared preload cache; no `Suspense` required). */
  loadingFallback?: ReactNode;

  /**
   * `"inert"` renders the module with action stubs that reject instead of
   * binding them to the server. Used when the shell renders a layout before
   * its view is committed (first-load loading state), where actions must not
   * run. Defaults to `"bound"`.
   */
  actionsMode?: "bound" | "inert";
};

type LoadState =
  | { status: "pending"; key: string }
  | { status: "ready"; key: string; definition: LoadedModule["definition"] }
  | { status: "error"; key: string; error: unknown };

function stateFromCache(moduleId: ModuleID, version: VersionString): LoadState {
  const key = `${moduleId}:${version}`;
  const cached = peekLoadedModule(moduleId, version);
  return cached
    ? { status: "ready", key, definition: cached.definition }
    : { status: "pending", key };
}

/**
 * Loads one module bundle from the CDN and renders `leaf` | `branch` | `layout`
 * with `actions` / `props` / `data` (+ `slots` | `children` as appropriate).
 *
 * Loads subscribe via {@link getModuleLoadPromise} (same cached promise as preload in `<ViewRenderer>`).
 *
 * View-level identity (viewId / viewVersion / apiBaseUrl) is read from
 * `<ViewIdentityProvider>`; mounting outside one throws when the loaded
 * module first calls `useClientActionMap`. `"inert"` mode needs no provider.
 *
 * `props` and `data` are passed by reference; the renderer does not mutate them.
 */
export function ModuleRenderer({
  moduleId,
  instanceKey,
  version,
  loader,
  props,
  data,
  slots,
  children,
  loadingFallback,
  actionsMode = "bound",
}: ModuleRendererProps): ReactNode {
  const [storedState, setState] = useState<LoadState>(() =>
    stateFromCache(moduleId, version),
  );
  const viewIdentity = useOptionalViewIdentity();
  const viewId = viewIdentity?.viewId;
  const viewVersion = viewIdentity?.viewVersion;
  // A moduleId/version change must not render the previous module's
  // definition for the commit before the effect runs.
  const state =
    storedState.key === `${moduleId}:${version}`
      ? storedState
      : stateFromCache(moduleId, version);

  useEffect(() => {
    let cancelled = false;
    const key = `${moduleId}:${version}`;
    const cached = peekLoadedModule(moduleId, version);
    if (!cached) {
      setState({ status: "pending", key });
    }

    void getModuleLoadPromise(loader, moduleId, version).then(
      (loaded) => {
        if (cancelled) {
          return;
        }
        setState({ status: "ready", key, definition: loaded.definition });
        if (viewId !== undefined && viewVersion !== undefined) {
          markModuleReadyForReadiness({ viewId, viewVersion, moduleId, version });
        }
      },
      (err: unknown) => {
        if (cancelled) {
          return;
        }
        setState({ status: "error", key, error: err });
      },
    );

    return () => {
      cancelled = true;
    };
  }, [moduleId, version, loader, viewId, viewVersion]);

  if (state.status === "pending") {
    return loadingFallback ?? null;
  }

  if (state.status === "error") {
    if (isModuleLoadError(state.error)) {
      const failureError = resolveFailureError(
        state.error,
        `Failed to load module "${moduleId}" (${version}).`,
      );
      reportFailureOnce(failureError, {
        tag: FAILURE_EVENT_TAGS.moduleBundleLoad,
        errorCode: state.error.code,
        message: failureError.message,
        error: failureError,
        moduleId: state.error.moduleId,
        moduleVersion: state.error.version,
      });
      markFailureReported(state.error);
    }
    throw state.error;
  }

  if (actionsMode === "inert") {
    return (
      <ModuleRendererInert
        definition={state.definition}
        props={props}
        data={data}
        slots={slots}
        children={children}
      />
    );
  }

  return (
    <ModuleRendererLoaded
      moduleVersion={version}
      instanceKey={instanceKey}
      definition={state.definition}
      props={props}
      data={data}
      slots={slots}
      children={children}
    />
  );
}

type ModuleRendererLoadedProps = Pick<
  ModuleRendererProps,
  "instanceKey" | "props" | "data" | "slots" | "children"
> & {
  moduleVersion: VersionString;
  definition: LoadedModule["definition"];
};

/**
 * Sub-component so `useClientActionMap` is only called once a definition exists.
 */
function ModuleRendererLoaded({
  moduleVersion,
  instanceKey,
  definition,
  props,
  data,
  slots,
  children,
}: ModuleRendererLoadedProps): ReactNode {
  const actions = useClientActionMap(definition, {
    moduleVersion,
    instanceKey,
  });

  // The renderer no longer wraps modules in `<EnrollmentProvider>` /
  // `<CarrierProvider>`. A module that reads carrier/enrollment context bundles
  // its own provider+hook and self-wraps its tree from `data` (the `store` still
  // arrives in `data`). Wrapping here would put the provider in the shell bundle
  // and the hook in the module bundle — the cross-bundle context mismatch this
  // change fixes.
  return renderModuleElement(definition, actions, props, data, slots, children);
}

function ModuleRendererInert({
  definition,
  props,
  data,
  slots,
  children,
}: Omit<ModuleRendererLoadedProps, "moduleVersion">): ReactNode {
  const actions = Object.fromEntries(
    Object.keys(definition.actions ?? {}).map(name => [
      name,
      () =>
        Promise.reject(
          new Error(
            `Action "${name}" on module "${definition.id}" is unavailable while the view is loading.`,
          ),
        ),
    ]),
  );

  return renderModuleElement(definition, actions, props, data, slots, children);
}

function renderModuleElement(
  definition: LoadedModule["definition"],
  actions: Record<string, (data: unknown) => Promise<unknown>>,
  props: unknown,
  data: unknown,
  slots: Record<string, ReactNode> | undefined,
  children: ReactNode,
): ReactNode {
  switch (definition.type) {
    case "leaf": {
      const Leaf = definition.component;
      return <Leaf actions={actions} props={props} data={data} />;
    }
    case "branch": {
      const Branch = definition.component;
      return (
        <Branch actions={actions} props={props} data={data} slots={slots ?? {}} />
      );
    }
    case "layout": {
      const Layout = definition.component;
      return (
        <Layout actions={actions} props={props} data={data}>
          {children}
        </Layout>
      );
    }
    case "error": {
      throw new Error(
        `Module "${definition.id}" has type "error" and cannot be mounted through ModuleRenderer. Error modules are rendered only by ShellErrorBoundary.`,
      );
    }
    case "loading": {
      throw new Error(
        `Module "${definition.id}" has type "loading" and cannot be mounted through ModuleRenderer. Loading modules are rendered only by the shell's loading host.`,
      );
    }
    default: {
      throw new Error("Unreachable: unexpected module definition shape");
    }
  }
}
